import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { useCookies } from "vue3-cookies";
import ErrorBus from "@/components/ErrorBus.vue";
import store from "@/store/index.js";
import { type OktaTokenBearer, resolveOktaAccessToken, shouldRefreshToken } from "@/util/auth";

const skipErrorBus = import.meta.env.VITE_APP_ENV === "production";

const { cookies } = useCookies();

const IMPERSONATION_HEADER = "Impersonation-Token";

type CurrentUser = OktaTokenBearer;

type AppStore = {
  getters?: {
    getCurrentUser?: CurrentUser;
  };
  dispatch: {
    (action: "refreshUserDataFromServer"): Promise<void>;
    (action: "logout", payload: { sessionExpired: boolean; reload: boolean }): unknown;
  };
};

// Resolved per call, never at module scope. `store/index.js` imports `./actions`, which imports
// this module, so on entry from the store side of that cycle the default export above is still
// initializing — reading it here would throw. Every caller below runs after boot, by which point
// the store is built. (The predecessor API.js was safe for the same reason: it only ever touched
// the store inside a `computed` getter.)
const appStore = (): AppStore => store as AppStore;

const getOktaAccessToken = (): string | undefined => resolveOktaAccessToken(appStore().getters?.getCurrentUser);

const clients = new Map<string, AxiosInstance>();
let refreshInFlight: Promise<void> | undefined;

const cacheKey = (baseUrl: string, withAuth: boolean): string => `${withAuth ? "auth" : "cookie"}:${baseUrl}`;

const attachImpersonationInterceptor = (client: AxiosInstance): void => {
  client.interceptors.request.use((request: AxiosRequestConfig): AxiosRequestConfig => {
    const token = cookies.get("impersonation_token");
    const headers = (request.headers ?? {}) as Record<string, string>;

    if (token) {
      headers[IMPERSONATION_HEADER] = token;
      request.headers = headers;
    } else {
      const { [IMPERSONATION_HEADER]: _impersonationToken, ...remainingHeaders } = headers;
      request.headers = remainingHeaders;
    }

    return request;
  });
};

const attachResponseInterceptor = (client: AxiosInstance): void => {
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<{ sentryError?: unknown }>) => {
      const errorCode = error?.response?.status;
      const config = error?.response?.config as (AxiosRequestConfig & { skipErrorBus?: boolean }) | undefined;
      const url = config?.url;
      const sentryError = error?.response?.data?.sentryError;

      if ((url === "/user" || url === "/user/refresh-okta-token") && errorCode === 401) {
        appStore().dispatch("logout", {
          sessionExpired: true,
          reload: true,
        });
      } else if (url === "/forms/current" && (errorCode === 401 || errorCode === 500)) {
        // An unresolvable current user is a 401; a 500 here is a genuine backend failure.
        // Both leave the app with no form-service identity, so bounce to the landing page.
        window.location.href = "/landing-page";
      } else if (!skipErrorBus && !config?.skipErrorBus && sentryError && errorCode !== 404) {
        (ErrorBus as unknown as { registerError: (err: unknown) => void }).registerError(sentryError);
      }

      return Promise.reject(error);
    },
  );
};

const createClient = (baseUrl: string, withAuth: boolean): AxiosInstance => {
  const client = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "platform-type": "web",
    },
  });

  if (withAuth) {
    client.interceptors.request.use(async (request: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
      let token = getOktaAccessToken();

      // Refresh lazily only when close to expiry to avoid timer drift.
      if (token && shouldRefreshToken(token)) {
        if (!refreshInFlight) {
          refreshInFlight = appStore()
            .dispatch("refreshUserDataFromServer")
            .catch(() => {
              // logout/session-expired handling happens inside the action.
            })
            .finally(() => {
              refreshInFlight = undefined;
            });
        }

        await refreshInFlight;
        token = getOktaAccessToken();
      }

      const headers = (request.headers ?? {}) as Record<string, string>;

      if (token) {
        headers.Authorization = `Bearer ${token}`;
        request.headers = headers;
      } else {
        const { Authorization, ...remainingHeaders } = headers;
        request.headers = remainingHeaders;
      }

      return request;
    });
  }

  attachImpersonationInterceptor(client);
  attachResponseInterceptor(client);
  return client;
};

export const getApiClientWithAuth = (baseUrl = "/api"): AxiosInstance => {
  const key = cacheKey(baseUrl, true);
  if (!clients.has(key)) clients.set(key, createClient(baseUrl, true));
  return clients.get(key)!;
};

export const getApiClient = (baseUrl = "/api"): AxiosInstance => {
  const key = cacheKey(baseUrl, false);
  if (!clients.has(key)) clients.set(key, createClient(baseUrl, false));
  return clients.get(key)!;
};
